import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { SK } from '../config/constants';
import { makeCollection, autoAdvanceStatus } from '../utils/products';
import { storeSave, storeLoad, saveProduct, saveProductsBulk, deleteProducts } from '../utils/storage';
import { useTenantConfig } from './useTenant';

// Owns all app data state, persistence, and real-time sync.
// The root component handles auth and rendering only.
export function useAppData(userId, tenantId) {
  const tenantConfig = useTenantConfig();
  const lsKey = tenantId ? `${SK}-${tenantId}` : SK;

  const [collections, setCollections]       = useState([makeCollection('Summer 2026')]);
  const [activeCollIdx, setActiveCollIdx]   = useState(() => {
    const saved = sessionStorage.getItem('activeCollIdx');
    return saved !== null ? Number(saved) : 0;
  });
  const [priceTable, setPriceTable]         = useState({});
  const [sizeTable, setSizeTable]           = useState({ bra: {}, clothes: {} });
  const [launchCalendar, setLaunchCalendar] = useState([]);
  const [saveStatus, setSaveStatus]         = useState('saved');
  const [editingProduct, setEditingProduct] = useState(null);

  const saveTimer         = useRef(null);
  const priceTableRef     = useRef({});
  const sizeTableRef      = useRef({ bra: {}, clothes: {} });
  const launchCalRef      = useRef([]);
  const editingProductRef = useRef(null);
  const hasLoadedRef      = useRef(false);

  // Keep refs in sync so debounced callbacks always read current values
  useEffect(() => { priceTableRef.current    = priceTable;     }, [priceTable]);
  useEffect(() => { sizeTableRef.current     = sizeTable;      }, [sizeTable]);
  useEffect(() => { launchCalRef.current     = launchCalendar; }, [launchCalendar]);
  useEffect(() => { editingProductRef.current = editingProduct; }, [editingProduct]);

  // Persist active collection index across page refreshes
  useEffect(() => { sessionStorage.setItem('activeCollIdx', activeCollIdx); }, [activeCollIdx]);

  // Load from storage — exactly once per session, scoped to this tenant.
  // hasLoadedRef guards against TOKEN_REFRESHED / INITIAL_SESSION firing a new user object
  // reference with the same underlying userId, which would re-run the effect and overwrite state.
  useEffect(() => {
    if (!userId || !tenantId) { hasLoadedRef.current = false; return; }
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    storeLoad(tenantId, lsKey).then(data => {
      if (!data) return;
      if (data.collections?.length) {
        const mapped = data.collections.map(c => ({ ...c, products: (c.products || []).map(p => autoAdvanceStatus(p, tenantConfig)) }));
        setCollections(mapped);
        setActiveCollIdx(idx => Math.min(idx, mapped.length - 1));
      }
      if (data.priceTable)     setPriceTable(data.priceTable);
      if (data.sizeTable)      setSizeTable(data.sizeTable);
      if (data.launchCalendar) setLaunchCalendar(data.launchCalendar);
    });
  }, [userId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time sync — keeps all open clients in sync when any user saves.
  // Collection and singleton-table subscriptions are filtered to this tenant.
  // Product subscriptions are implicitly scoped: updates only apply if the
  // collection already exists in this tenant's loaded state.
  useEffect(() => {
    if (!userId || !tenantId) return;
    const channel = supabase
      .channel('app-data-changes')

      // ── products ──────────────────────────────────────────────
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, async (payload) => {
        if (payload.eventType === 'DELETE') {
          const deletedId = payload.old?.id;
          if (!deletedId) return;
          setCollections(prev => prev.map(c => ({ ...c, products: c.products.filter(p => p.id !== deletedId) })));
          return;
        }
        const productId    = payload.new?.id;
        const collectionId = payload.new?.collection_id;
        if (!productId || !collectionId) return;
        if (editingProductRef.current?.id === productId) return;
        const { data: row } = await supabase
          .from('products').select('id, collection_id, data').eq('id', productId).maybeSingle();
        if (!row?.data) return;
        setCollections(prev => {
          // Only apply if this collection belongs to this tenant's loaded state
          if (!prev.some(c => c.id === collectionId)) return prev;
          return prev.map(c => {
            if (c.id !== collectionId) return c;
            const updated = autoAdvanceStatus(row.data, tenantConfig);
            const exists  = c.products.some(p => p.id === productId);
            return {
              ...c,
              products: exists
                ? c.products.map(p => p.id === productId ? updated : p)
                : [...c.products, updated],
            };
          });
        });
      })

      // ── collections ── filtered to this tenant ────────────────
      .on('postgres_changes', { event: '*', schema: 'public', table: 'collections', filter: `tenant_id=eq.${tenantId}` }, async (payload) => {
        if (payload.eventType === 'DELETE') {
          const deletedAppId = payload.old?.app_id;
          if (!deletedAppId) return;
          setCollections(prev => {
            const next = prev.filter(c => c.id !== deletedAppId);
            setActiveCollIdx(idx => Math.min(idx, Math.max(0, next.length - 1)));
            return next.length > 0 ? next : prev;
          });
          return;
        }
        const appId = payload.new?.app_id;
        if (!appId) return;
        if (payload.eventType === 'INSERT') {
          setCollections(prev => {
            if (prev.some(c => c.id === appId)) return prev;
            return [...prev, {
              id: appId, name: payload.new.name ?? 'New Collection',
              month: payload.new.month ?? null, springboard: payload.new.springboard ?? null,
              products: [],
            }];
          });
          return;
        }
        setCollections(prev => prev.map(c =>
          c.id !== appId ? c : {
            ...c,
            name:        payload.new.name        ?? c.name,
            month:       payload.new.month       ?? c.month,
            springboard: payload.new.springboard ?? c.springboard,
          }
        ));
      })

      // ── price_table / size_table / launch_calendar ── filtered to this tenant ─
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'price_table', filter: `tenant_id=eq.${tenantId}` }, async () => {
        const { data } = await supabase.from('price_table').select('data').eq('tenant_id', tenantId).maybeSingle();
        if (data?.data) setPriceTable(data.data);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'size_table', filter: `tenant_id=eq.${tenantId}` }, async () => {
        const { data } = await supabase.from('size_table').select('data').eq('tenant_id', tenantId).maybeSingle();
        if (data?.data) setSizeTable(data.data);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'launch_calendar', filter: `tenant_id=eq.${tenantId}` }, async () => {
        const { data } = await supabase.from('launch_calendar').select('events').eq('tenant_id', tenantId).maybeSingle();
        if (data?.events) setLaunchCalendar(data.events);
      })

      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [userId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save helpers ──────────────────────────────────────────────

  // Write full state to localStorage before every Supabase save so a hard refresh within
  // the debounce window can recover the latest state.
  const writeLS = useCallback((colls, collIdx) => {
    try {
      localStorage.setItem(lsKey, JSON.stringify({
        collections: colls, activeCollIdx: collIdx,
        priceTable: priceTableRef.current, sizeTable: sizeTableRef.current, launchCalendar: launchCalRef.current,
        _savedAt: Date.now(),
      }));
    } catch (_) {}
  }, [lsKey]);

  // Debounced metadata save — pt/st/cal read from refs so no stale closure issues.
  const save = useCallback((colls, collIdx, pt, st, cal) => {
    setSaveStatus('saving');
    const pt_  = pt  ?? priceTableRef.current;
    const st_  = st  ?? sizeTableRef.current;
    const cal_ = cal ?? launchCalRef.current;
    writeLS(colls, collIdx);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      storeSave({ collections: colls, activeCollIdx: collIdx, priceTable: pt_, sizeTable: st_, launchCalendar: cal_, tenantId, lsKey })
        .then(() => setSaveStatus('saved'))
        .catch(e => { console.error('[save]', e); setSaveStatus('saved'); });
    }, 700);
  }, [writeLS, tenantId, lsKey]);

  // ── Public update functions ───────────────────────────────────

  const updateCollections = (newColls, newIdx = activeCollIdx) => {
    setCollections(newColls);
    setActiveCollIdx(newIdx);
    save(newColls, newIdx, priceTable);
  };

  const updatePriceTable = (pt) => {
    setPriceTable(pt);
    save(collections, activeCollIdx, pt);
  };

  const updateSizeTable = (st) => {
    setSizeTable(st);
    save(collections, activeCollIdx, undefined, st);
  };

  const updateCalendar = (events) => {
    setLaunchCalendar(events);
    save(collections, activeCollIdx, undefined, undefined, events);
  };

  const activeCollection = collections[activeCollIdx] || collections[0];

  const updateProducts = (products) => {
    const newColls = collections.map((c, i) => i === activeCollIdx ? { ...c, products } : c);
    setCollections(newColls);
    const prevIds    = new Set((activeCollection?.products || []).map(p => p.id));
    const nextIds    = new Set(products.map(p => p.id));
    const removedIds = [...prevIds].filter(id => !nextIds.has(id));
    if (removedIds.length) deleteProducts(removedIds);
    // Write immediately (no debounce) — bulk changes must survive a refresh without relying
    // on the debounce window or localStorage being intact.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus('saving');
    writeLS(newColls, activeCollIdx);
    Promise.all([
      storeSave({ collections: newColls, activeCollIdx, priceTable: priceTableRef.current, sizeTable: sizeTableRef.current, launchCalendar: launchCalRef.current, tenantId, lsKey }),
      saveProductsBulk(products, activeCollection.id),
    ]).then(() => setSaveStatus('saved'))
      .catch(e => { console.error('[updateProducts]', e); setSaveStatus('saved'); });
    if (editingProduct) {
      const updated = products.find(p => p.id === editingProduct.id);
      if (updated) setEditingProduct(updated);
    }
  };

  const updateProduct = (p) => {
    const advanced = autoAdvanceStatus(p, tenantConfig);
    const products = activeCollection.products.map(existing => existing.id === advanced.id ? advanced : existing);
    const newColls = collections.map((c, i) => i === activeCollIdx ? { ...c, products } : c);
    setCollections(newColls);
    if (editingProduct?.id === advanced.id) setEditingProduct(advanced);
    // Per-product debounced save — only touches this product's row, preventing concurrent
    // edits on different products from overwriting each other.
    saveProduct(advanced, activeCollection.id);
    // Write localStorage immediately so a hard reload within the debounce window recovers
    // the edit (storeLoad removes localStorage after loading from Supabase).
    writeLS(newColls, activeCollIdx);
    setSaveStatus('saving');
    setTimeout(() => setSaveStatus('saved'), 700);
  };

  // Latest collections for work that finishes long after the render that started it (a
  // background video crop): updateProduct's closure would write over anything changed since.
  const collectionsRef = useRef(collections);
  collectionsRef.current = collections;
  const activeIdxRef = useRef(activeCollIdx);
  activeIdxRef.current = activeCollIdx;

  // Applies fn to one product, in whichever collection holds it, against current state.
  const patchProduct = (productId, fn) => {
    const colls = collectionsRef.current;
    const ci = colls.findIndex(c => (c.products || []).some(p => p.id === productId));
    if (ci < 0) return false;
    const cur = colls[ci].products.find(p => p.id === productId);
    const next = autoAdvanceStatus(fn(cur), tenantConfig);
    const newColls = colls.map((c, i) => i === ci ? { ...c, products: c.products.map(p => p.id === productId ? next : p) } : c);
    collectionsRef.current = newColls;
    setCollections(newColls);
    if (editingProductRef.current?.id === productId) setEditingProduct(next);
    saveProduct(next, colls[ci].id);
    writeLS(newColls, activeIdxRef.current);
    return true;
  };

  const switchCollection = (idx) => {
    setActiveCollIdx(idx);
    setEditingProduct(null);
  };

  return {
    collections,
    activeCollIdx,
    activeCollection,
    priceTable,
    sizeTable,
    launchCalendar,
    saveStatus,
    editingProduct,
    setEditingProduct,
    updateCollections,
    updateProducts,
    updateProduct,
    patchProduct,
    updatePriceTable,
    updateSizeTable,
    updateCalendar,
    switchCollection,
  };
}
